import type {
  InfiniteData,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { type RefObject, type UIEventHandler, useMemo } from "react";
import { SimpleTable } from "@/core/components/simple-table";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { ApPage } from "@/accounts-payable/types";
import { flattenPages } from "@/accounts-payable/utils";
import { cn } from "@/core/utils/utils";
import { LoadMoreButton } from "./shared";

type ListQuery<TData> = Pick<
  UseInfiniteQueryResult<InfiniteData<ApPage<TData>>>,
  | "data"
  | "isPending"
  | "isError"
  | "isPlaceholderData"
  | "hasNextPage"
  | "fetchNextPage"
  | "isFetching"
  | "isFetchingNextPage"
>;

export type ListTableMessage = { title: string; body: string };
type Message = ListTableMessage;

const TableMessage = ({ title, body }: Message) => (
  <Stack gap="sm" align="center" fullWidth className="py-12 text-center">
    <Typography variant="subtitleW2">{title}</Typography>
    <Typography textColor="tertiary">{body}</Typography>
  </Stack>
);

/**
 * The AP list tables (Invoices, Receipts, Emails, Vendors): the table card,
 * plus its empty, error and load-more states.
 */
export const ListTableCard = <TData extends { id: string }>({
  query,
  columns,
  selectedId,
  onSelect,
  empty,
  error,
  className,
  viewportRef,
  onViewportScroll,
  awaitingPosition = false,
  restoring = false,
}: {
  query: ListQuery<TData>;
  columns: ColumnDef<TData>[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  empty: Message;
  error: Message;
  className?: string;
  viewportRef?: RefObject<HTMLDivElement>;
  onViewportScroll?: UIEventHandler<HTMLDivElement>;
  awaitingPosition?: boolean;
  restoring?: boolean;
}) => {
  const {
    data: pages,
    isPending,
    isError,
    isPlaceholderData,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = query;
  // Stable rows prevent the table from rebuilding its row model on each render.
  const data = useMemo(() => flattenPages(pages?.pages), [pages]);
  // A new filter keeps the previous result on screen until its own arrives
  // (placeholderData). Rows from before are fine to show meanwhile; an empty
  // result from before is not: it would announce "nothing today" before the
  // request for today has even answered. Show loading instead.
  const awaitingNewResult = !!isPlaceholderData && data.length === 0;
  const isLoading = isPending || awaitingNewResult || awaitingPosition;
  const showError = isError && (data.length === 0 || restoring);

  return (
    <Card
      className={cn(
        "mt-4 flex min-h-0 w-full flex-1 flex-col overflow-hidden border-stroke-tertiary shadow-none",
        className,
      )}
    >
      {/* Only a *successful* empty load is "empty": a failed fetch is an error,
        and a query waiting on the business id hasn't loaded yet (it isn't
        `isLoading` then, so that flag alone flashed the empty state). */}
      {showError ? (
        <TableMessage {...error} />
      ) : !isLoading && data.length === 0 ? (
        <TableMessage {...empty} />
      ) : (
        <>
          {/* Full-bleed rows; the outer columns take the card's 24px inset. */}
          <SimpleTable
            data={data}
            columns={columns}
            isLoading={isLoading}
            viewportRef={viewportRef}
            onViewportScroll={onViewportScroll}
            getRowId={(row) => row.id}
            selectedRowId={selectedId}
            onRowClick={(row) => onSelect(row.id)}
            className="min-h-0 w-full flex-1 [&_td:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:first-child]:pl-6 [&_th:last-child]:pr-6"
            scrollContentStyle={{ minWidth: 900 }}
          />
          {hasNextPage && (
            <Stack fullWidth className="flex-none py-4">
              <LoadMoreButton
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
                disabled={isFetching}
              />
            </Stack>
          )}
        </>
      )}
    </Card>
  );
};
